import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import PlanUpgradeFlow from 'dashboard/components/PlanUpgradeFlow';
import { Blueprint } from 'dashboard/models/blueprint';
import { AvailableOrganizationPlan, OrganizationPlan } from 'dashboard/models/organization-plan';
import { OrganizationPlanMigration } from 'dashboard/models/organization-plan-migration';
import { RootState } from 'dashboard/redux/store';
import CurrentPlanCard from 'dashboard/views/DashboardOrganizationBillingOverviewView/CurrentPlanCard';
import PlansTable from 'dashboard/views/DashboardOrganizationBillingOverviewView/PlansTable';
import Spacer from 'design-system/components/Spacer';
import { Heading } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';

export interface DashboardOrganizationBillingOverviewViewProps {
  currentOrganizationPlan: OrganizationPlan | null;
  blueprints: Blueprint[];
  loadOrganization: () => Promise<void>;
  loadAvailablePlans: () => Promise<AvailableOrganizationPlan[]>;
  handleCreateOrganizationPlanMigration: (
    inquiryId: string,
    selectedPlanKey: string,
  ) => Promise<OrganizationPlanMigration>;
  handleLoadOrganizationPlanMigration: (inquiryId: string) => Promise<OrganizationPlanMigration>;
  updatePayment: (stripeId: string) => Promise<void>;
  loadBlueprints: () => Promise<void>;
  showMessage: (message: string) => void;
  openTalkToSalesLink: () => void;
}

const DashboardOrganizationBillingOverviewView = ({
  currentOrganizationPlan,
  blueprints,
  loadOrganization,
  loadAvailablePlans,
  handleCreateOrganizationPlanMigration,
  updatePayment,
  loadBlueprints,
  showMessage,
  openTalkToSalesLink,
}: DashboardOrganizationBillingOverviewViewProps) => {
  const [showModal, { enable: openModal, disable: hideModal }] = useToggle(false);
  const [requestedOrganizationPlan, setRequestedOrganizationPlan] =
    useState<AvailableOrganizationPlan | null>(null);

  const [availablePlans, setAvailablePlans] = useState<AvailableOrganizationPlan[]>([]);
  useEffect(() => {
    const fetchOrganizationAndAvailablePlans = async () => {
      // We reload the org here in case the org's plan or plan migrations have changed since
      // the org was initially loaded into Redux state when the app was loaded.
      // This could happen, for example, if the org just signed up for Persona and requested a
      // Essential plan trial, but the creation of the plan migration was delayed.
      await loadOrganization();

      const availablePlans = await loadAvailablePlans();
      setAvailablePlans(availablePlans);
    };

    fetchOrganizationAndAvailablePlans();
  }, [loadAvailablePlans, loadOrganization]);

  const pendingOrganizationPlanMigration = useSelector(
    (state: RootState) => state.organization.pendingOrganizationPlanMigration,
  );

  return (
    <>
      {currentOrganizationPlan && requestedOrganizationPlan && (
        <ModalWrapper isOpen={showModal} handleCloseModal={hideModal}>
          <PlanUpgradeFlow
            requestedOrganizationPlan={requestedOrganizationPlan}
            blueprints={blueprints}
            loadBlueprints={loadBlueprints}
            requestedFeatureName='' // Empty because there is not a specific feature when upgrading plan from this page
            handleSubmitBillingInfo={updatePayment}
            handleCreateOrganizationPlanMigration={handleCreateOrganizationPlanMigration}
            showMessage={showMessage}
            onNavBack={hideModal}
            onClose={hideModal}
          />
        </ModalWrapper>
      )}
      <PageWrapper>
        <Heading size='sm'>Current Plan</Heading>
        <Spacer height={1} />
        <CurrentPlanCard
          currentOrganizationPlan={currentOrganizationPlan}
          pendingOrganizationPlanMigration={pendingOrganizationPlanMigration}
        />
        <Spacer height={2} />

        {currentOrganizationPlan?.attributes['able-to-request-plan-changes-in-dashboard'] && (
          <>
            <Heading size='sm'>All Plans</Heading>
            <Spacer height={1} />
            <PlansTable
              currentOrganizationPlanKey={currentOrganizationPlan.attributes.key}
              availablePlans={availablePlans}
              hasPendingPlanMigration={
                !!pendingOrganizationPlanMigration?.attributes?.['requested-plan-key']
              }
              openPlanSelectionModal={(requestedOrganizationPlan: AvailableOrganizationPlan) => {
                setRequestedOrganizationPlan(requestedOrganizationPlan);
                openModal();
              }}
              openTalkToSalesLink={openTalkToSalesLink}
            />
          </>
        )}
      </PageWrapper>
    </>
  );
};

const PageWrapper = styled.div`
  max-width: 1200px;
  margin: auto;
`;

export default DashboardOrganizationBillingOverviewView;
